import '@livekit/components-styles'
import { LiveKitRoom as LiveKitRoomComponent } from '@livekit/components-react'
import { Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useFinishPitchMutation, useGetDocumentDownloadUrlMutation } from '../../../hooks/api'
import type { LiveKitRoomProps } from '../types/pitch.types'
import { VideoConference } from './video-conference'

export function LiveKitRoom({ pitchSession, mediaSettings }: LiveKitRoomProps) {
  const finishPitch = useFinishPitchMutation()
  const downloadUrlMutation = useGetDocumentDownloadUrlMutation()
  const [pitchDeckUrl, setPitchDeckUrl] = useState<string | null>(null)

  const token = pitchSession.sessionToken
  const serverUrl = import.meta.env.VITE_LIVEKIT_URL

  // Fetch pitch deck download URL when component mounts
  // biome-ignore lint/correctness/useExhaustiveDependencies: mutation.mutate is stable
  useEffect(() => {
    if (pitchSession.pitchDeckId) {
      downloadUrlMutation.mutate(
        { path: { id: pitchSession.pitchDeckId } },
        {
          onSuccess: (response) => {
            if (response.status === 'ok') {
              setPitchDeckUrl(response.data.downloadUrl)
            }
          },
        }
      )
    }
  }, [pitchSession.pitchDeckId])

  const handleDisconnect = async () => {
    await finishPitch.mutateAsync({
      path: { pitchId: pitchSession.id },
    })
  }

  const roomOptions = useMemo(
    () => ({
      audioCaptureDefaults: mediaSettings.audioDeviceId
        ? { deviceId: mediaSettings.audioDeviceId }
        : undefined,
      videoCaptureDefaults: mediaSettings.videoDeviceId
        ? { deviceId: mediaSettings.videoDeviceId }
        : undefined,
    }),
    [mediaSettings.audioDeviceId, mediaSettings.videoDeviceId]
  )

  if (!token || !serverUrl || !pitchDeckUrl) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <div className="rounded-xl border border-border bg-input/50 p-6 text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-[#f97316]/10">
            <Loader2 className="size-6 text-[#f97316] animate-spin" />
          </div>
          <h2 className="text-lg font-semibold text-white">Connecting</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Preparing your pitch deck and connecting
            <br />
            you with your AI investor.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-background">
      <LiveKitRoomComponent
        token={token}
        serverUrl={serverUrl}
        connect={true}
        audio={mediaSettings.audioEnabled}
        video={mediaSettings.videoEnabled}
        options={roomOptions}
        className="h-screen"
        onDisconnected={handleDisconnect}
      >
        <VideoConference pitchDeckUrl={pitchDeckUrl} />
      </LiveKitRoomComponent>
    </div>
  )
}
